import React, { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

export interface AgentPhase {
  key: string;
  label: string;
}

interface Props {
  icon: React.ReactNode;
  title: string;
  phases: AgentPhase[];
  /** Índice da fase ativa; >= phases.length = tudo concluído. */
  activeIndex: number;
  /** 0..1; barra some quando done/error. */
  progress: number;
  done?: boolean;
  error?: boolean;
  /** Epoch ms do INÍCIO do run (estado por-chat). Sem ele, ancora no mount —
   * e o relógio zera quando o card remonta (ex.: trocar de chat e voltar). */
  startedAt?: number;
  /** Conteúdo extra do header (stats, chevron) — alinhado à direita, antes do tempo. */
  headerExtra?: React.ReactNode;
  onHeaderClick?: () => void;
  children?: React.ReactNode;
}

const formatElapsed = (s: number): string => {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${String(s % 60).padStart(2, '0')}s`;
};

/**
 * Card de progresso dos sub-agentes (panorama / pesquisa aprofundada):
 * header (ícone + título + tempo decorrido), barra fina e stepper de fases
 * (feita = ponto verde; ativa = ponto verde + rótulo forte; futura = cinza).
 * O tempo conta a partir da montagem e congela em done/error; cards
 * reidratados do histórico (já done) não mostram tempo nem barra.
 */
const AgentProgressCard: React.FC<Props> = ({
  icon,
  title,
  phases,
  activeIndex,
  progress,
  done = false,
  error = false,
  startedAt,
  headerExtra,
  onHeaderClick,
  children,
}) => {
  const startRef = useRef<number>(startedAt || Date.now());
  const [elapsed, setElapsed] = useState(() =>
    Math.max(0, Math.floor((Date.now() - startRef.current) / 1000)),
  );
  const mountedDoneRef = useRef<boolean>(done || error);
  const running = !done && !error;

  useEffect(() => {
    // startedAt pode chegar/atualizar depois do mount (estado por-chat).
    if (startedAt && startedAt !== startRef.current) {
      startRef.current = startedAt;
      setElapsed(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    }
  }, [startedAt]);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      setElapsed(Math.max(0, Math.floor((Date.now() - startRef.current) / 1000)));
    }, 1000);
    return () => clearInterval(t);
  }, [running]);

  const pct = Math.min(100, Math.max(2, Math.round((progress || 0) * 100)));
  const showElapsed = !mountedDoneRef.current;

  return (
    <Card data-testid="agent-progress-card">
      <Header onClick={onHeaderClick} $clickable={!!onHeaderClick}>
        <IconWrap $pulse={running}>{icon}</IconWrap>
        <Title>{title}</Title>
        <Spacer />
        {headerExtra}
        {showElapsed && (
          <Elapsed data-testid="agent-progress-elapsed">{formatElapsed(elapsed)}</Elapsed>
        )}
      </Header>
      {running && (
        <Track data-testid="agent-progress-bar">
          <Fill style={{ width: `${pct}%` }} $error={error} />
        </Track>
      )}
      <Stepper>
        {phases.map((p, i) => {
          const state = done ? 'done'
            : error && i === activeIndex ? 'error'
            : i < activeIndex ? 'done'
            : i === activeIndex ? 'active'
            : 'todo';
          return (
            <PhaseItem key={p.key} data-testid={`phase-${p.key}`} data-state={state} $state={state}>
              <Dot $state={state} />
              {p.label}
            </PhaseItem>
          );
        })}
      </Stepper>
      {children}
    </Card>
  );
};

export default AgentProgressCard;

const pulse = keyframes`
  0%, 100% { opacity: 0.7; }
  50%      { opacity: 1; }
`;

const Card = styled.div`
  /* Largura fixa no container: o card não pode encolher/esticar conforme o
     texto da etapa corrente muda (ficava "pulando" a cada consulta). */
  width: 100%;
  box-sizing: border-box;
  border: 1px solid rgba(127, 127, 127, 0.28);
  border-radius: 12px;
  padding: 12px 16px 10px;
  margin: 8px 0;
  font-family: 'Sora', 'Lexend', sans-serif;
  color: ${({ theme }) => theme.font_color || 'inherit'};
  background: rgba(127, 127, 127, 0.04);
`;

const Header = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  user-select: none;
`;

const IconWrap = styled.span<{ $pulse: boolean }>`
  display: inline-flex;
  align-items: center;
  opacity: 0.8;
  ${({ $pulse }) => $pulse && css`animation: ${pulse} 2.8s ease-in-out infinite;`}
`;

const Title = styled.span`
  font-weight: 600;
  font-size: 0.92rem;
`;

const Spacer = styled.span`
  flex: 1;
`;

const Elapsed = styled.span`
  font-size: 0.8rem;
  opacity: 0.55;
  font-variant-numeric: tabular-nums;
`;

const Track = styled.div`
  height: 6px;
  border-radius: 3px;
  background: rgba(127, 127, 127, 0.16);
  margin: 10px 0 8px;
  overflow: hidden;
`;

const Fill = styled.div<{ $error?: boolean }>`
  height: 100%;
  border-radius: 3px;
  background: ${({ $error }) => ($error ? '#dc2626' : '#047857')};
  transition: width 0.6s ease;
  animation: ${pulse} 2.4s ease-in-out infinite;
`;

const Stepper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-top: 8px;
  font-size: 0.82rem;
`;

const PhaseItem = styled.span<{ $state: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  ${({ $state }) =>
    $state === 'active'
      ? css`font-weight: 600; opacity: 1;`
      : $state === 'done'
      ? css`opacity: 0.75;`
      : $state === 'error'
      ? css`font-weight: 600; color: #dc2626; opacity: 1;`
      : css`opacity: 0.42;`}
`;

const Dot = styled.span<{ $state: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $state }) =>
    $state === 'done' || $state === 'active' ? '#047857'
    : $state === 'error' ? '#dc2626'
    : 'rgba(127, 127, 127, 0.45)'};
`;
